/**
 * Suggested asks — the chips that give a blank composer somewhere to start.
 *
 * Shared by every Benny entry point (the web Today ask bar, the Conversations
 * welcome, the mobile home surface) so the same suggestion is never phrased
 * two ways. Each surface still decides how many chips it shows; only the
 * WORDING, the truncation and the slot order live here.
 */

/**
 * Fallbacks when the day is quiet. `liveAsks` below swaps in suggestions
 * built from what is actually waiting — a chip should read like a real ask,
 * not a category label.
 */
export const FALLBACK_ASKS = [
  'What needs me today?',
  'Show my overdue tasks',
  // Both answerable by a tool in one turn. The previous two ("Draft an
  // invoice for last month's work", "Which clients haven't heard from us
  // in 30 days?") turned into a hand-typed invoice form and a refusal (live
  // run 07 §A). The trailing "…" is the prefill convention: the user names
  // the client before sending.
  'Show my open tickets',
  'Catch me up on a client…',
] as const

/**
 * A chip is a one-line ask, so its label is capped — but on a WORD boundary.
 * Slicing mid-word ("Staging environment login brok…") reads like a bug; the
 * cut lands on the last space before the cap instead.
 *
 * 44 rather than a flat 40 so the approvals ask ("Review the 128 entries
 * pending my approval") survives whole at any realistic count — a truncated
 * verb phrase loses the meaning the chip exists to carry.
 */
export const MAX_CHIP_LABEL = 44

export function truncateChipLabel(label: string, max = MAX_CHIP_LABEL): string {
  if (label.length <= max) return label
  const clipped = label.slice(0, max)
  const lastSpace = clipped.lastIndexOf(' ')
  // Only honour the boundary when it leaves a readable stub; a single very
  // long word falls back to a hard cut rather than to nothing.
  const cut = lastSpace > max / 2 ? clipped.slice(0, lastSpace) : clipped
  return `${cut.replace(/[\s,;:.\-–—]+$/, '')}…`
}

/** What the day itself has to offer. Keys are absent when there's nothing real. */
export interface LiveAsks {
  approvals?: string
  topTicket?: string
}

/**
 * The asks that come from the day itself. A key is left off where there's
 * nothing genuine to ask about — callers fall back rather than inventing a
 * number.
 */
export function liveAsks(input: {
  pendingApprovals: number
  topTicketTitle?: string | null
}): LiveAsks {
  const asks: LiveAsks = {}
  if (input.pendingApprovals > 0) {
    // Phrased as the approver's ask — "pending my approval" steers Benny to
    // the approvals queue, not the user's own My Time list.
    asks.approvals = truncateChipLabel(
      `Review the ${input.pendingApprovals} ${
        input.pendingApprovals === 1 ? 'entry' : 'entries'
      } pending my approval`,
    )
  }
  if (input.topTicketTitle) {
    asks.topTicket = truncateChipLabel(`Catch me up on ${input.topTicketTitle}`)
  }
  return asks
}

/**
 * Fill a surface's ask slots: the ask that always applies, then approvals,
 * then the top ticket — each falling back to a generic ask when there is
 * nothing real to put there — then the remaining fallbacks up to `count`.
 * Fixed slots mean the bar never reflows as the day changes.
 */
export function buildSuggestedAsks(live: LiveAsks, count = 3): string[] {
  const asks: string[] = []
  const push = (ask: string | undefined) => {
    if (!ask || asks.length >= count || asks.includes(ask)) return
    asks.push(ask)
  }

  push(FALLBACK_ASKS[0])
  push(live.approvals ?? FALLBACK_ASKS[1])
  push(live.topTicket ?? FALLBACK_ASKS[2])
  for (const fallback of FALLBACK_ASKS) push(fallback)

  return asks
}
